import { groupBy, isEqual, omit, values } from "lodash";
import { PropertySource } from "../services/api/entity-mapping";
import {
  BizFieldInfo,
  BizServiceMetric,
  EntityMeta,
  FieldPickerContextDTO,
  FieldPickerOptionData,
  FieldSchemaResponse,
  UIUserService,
  UserServiceField,
  UserServiceFieldWithMeta,
  UserServiceMetric,
  UserServiceTuple,
  UserServiceWithFields
} from "../services/api/explore";
import { CoreDataTypes, Entity, logger } from "../core";
import { FieldPickerUtils } from "../utils/FieldPickerUtils";
import { ALL_USERSERVICES_ENTITY_TYPE_ID } from "../utils/ExploreUtils";
import { ALLOWED_DATA_TYPES, ENTITY_DATA_TYPE } from "./constants";
import { FieldPickerRow } from "./PickerTypes";
import { compare, getUserServiceFieldDatatype, isEntityField } from "./utils";

export class FieldPickerModel {
  context: FieldPickerContextDTO;
  private bizFields: BizFieldInfo[];
  private usFields: UserServiceFieldWithMeta[];
  private usEntityFields: UserServiceFieldWithMeta[];
  private spanFields: UserServiceFieldWithMeta[];
  private journeyFields: UserServiceFieldWithMeta[];
  private groupByUSFields: UserServiceFieldWithMeta[];
  private bizMetrics: Array<BizServiceMetric | UserServiceMetric>;
  private perfMetrics: Array<BizServiceMetric | UserServiceMetric>;
  private entityMap: Record<string, EntityMeta>;
  private readonly usfOmitProps: Array<keyof UserServiceField> = [
    "allUserService",
    "userServices",
    "bizEntityFieldName",
    "displayBizEntityFieldName",
    "isAvailableInTenantContext"
  ];

  constructor(context?: FieldPickerContextDTO) {
    this.context = context;
    this.bizFields = [];
    this.usFields = [];
    this.usEntityFields = [];
    this.spanFields = [];
    this.perfMetrics = [];
    this.bizMetrics = [];
    this.entityMap = {};
    this.groupByUSFields = [];
  }

  init(data: FieldSchemaResponse) {
    if (data?.bizFields) {
      this.bizFields = data.bizFields.filter(field =>
        ALLOWED_DATA_TYPES.includes(field.bizField?.entityField?.propType)
      );
    }
    if (data?.userServiceFields) {
      this.usEntityFields = this.filterEntityUsFields(data?.userServiceFields);
      this.usFields = this.filterBizUsFields(data?.userServiceFields);
      this.spanFields = this.filterSpanUSFields(data?.userServiceFields);
      this.journeyFields = this.filterJourneyUSFields(data?.userServiceFields);
      this.groupByUSFields = this.filterGroupByUSFields(data.userServiceFields);
    }
    this.perfMetrics = this.getPerfMetrics(data?.bizMetrics, data?.userServiceMetrics);
    this.bizMetrics = this.getBizMetrics(data?.bizMetrics, data?.userServiceMetrics);

    if (data?.resultMetadata) {
      this.entityMap = data.resultMetadata.resultEntityMap;
    }
  }

  getUserServices(userServiceField: UserServiceField): Entity[] {
    let userServiceList: UserServiceTuple[];
    if (userServiceField.allUserService === true) {
      const { fieldName, dataType, entityField } = userServiceField;
      let allFields: UserServiceFieldWithMeta[] = [];
      allFields = allFields.concat(this.spanFields);
      allFields = allFields.concat(this.usEntityFields);
      allFields = allFields.concat(this.usFields);
      const searchObj = allFields.find(
        obj =>
          obj.userServiceField.fieldName === fieldName &&
          obj.userServiceField.dataType === dataType &&
          isEqual(obj.userServiceField?.entityField, entityField)
      );
      userServiceList = searchObj?.userServiceField.userServices;
    } else {
      userServiceList = userServiceField.userServices;
    }
    return this.getEntityUserServicesFromTuple(userServiceList);
  }

  // get user services from type UserServiceTuple into Entity[]
  getEntityUserServicesFromTuple(userServiceList: UserServiceTuple[]) {
    if (!userServiceList) {
      return [];
    }
    const userServices: Entity[] = [];
    userServiceList
      .map(us => us.userServiceEntityId)
      .forEach(id => {
        if (this.entityMap[id]) {
          userServices.push({
            id: this.entityMap[id]?.entityId,
            name: this.entityMap[id]?.name
          });
        }
      });
    return userServices;
  }

  getEntityMap() {
    return this.entityMap;
  }

  getUsName(usId: string) {
    return this.entityMap[usId]?.name || usId;
  }

  getUsFieldSources(field: UserServiceField) {
    const usIdToUSTupleMap = groupBy(field.userServices, us => us.userServiceEntityId);
    return Object.keys(usIdToUSTupleMap).map(usId => {
      const rootApiIds = usIdToUSTupleMap[usId].map(us => us.rootApi).filter(Boolean);
      const source: UIUserService = {
        userService: {
          id: usId,
          name: this.entityMap[usId]?.name ?? usId
        },
        rootApis: rootApiIds.map(rootapiId => ({
          id: rootapiId,
          name: this.entityMap[rootapiId]?.name ?? rootapiId
        }))
      };
      return source;
    });
  }

  getUserServiceEntityFieldsTableData(
    selectedOptions: FieldPickerOptionData[],
    skipOptions?: FieldPickerOptionData[],
    customUserServiceFields?: UserServiceFieldWithMeta[],
    showCommonUSFieldsOnly?: boolean
  ) {
    const nonExistingFields = (customUserServiceFields || []).filter(usField => {
      const matchingUSField = this.usEntityFields.find(
        eUsField => eUsField.userServiceField.fieldName === usField.userServiceField.fieldName
      );
      return !matchingUSField;
    });
    const fields = [...this.usEntityFields, ...nonExistingFields];
    const filterFields =
      showCommonUSFieldsOnly && this.context.userServices?.length
        ? fields.filter(field =>
            this.isContainingAllCommonUserServices(field.userServiceField.userServices, this.context.userServices)
          )
        : fields;
    return this.getFieldsTableData(filterFields, selectedOptions, skipOptions);
  }

  getUserServiceFieldsTableData(
    selectedOptions: FieldPickerOptionData[],
    skipOptions?: FieldPickerOptionData[],
    showCommonUSFieldsOnly?: boolean
  ) {
    const filteredFields =
      showCommonUSFieldsOnly && this.context.userServices?.length
        ? this.usFields.filter(field =>
            this.isContainingAllCommonUserServices(field.userServiceField.userServices, this.context.userServices)
          )
        : this.usFields;
    return this.getFieldsTableData(filteredFields, selectedOptions, skipOptions);
  }

  getUserServiceSpansTableData(
    selectedOptions: FieldPickerOptionData[],
    skipOptions?: FieldPickerOptionData[],
    showCommonUSFieldsOnly?: boolean
  ) {
    const filteredFields =
      showCommonUSFieldsOnly && this.context.userServices?.length
        ? this.spanFields.filter(field =>
            this.isContainingAllCommonUserServices(field.userServiceField.userServices, this.context?.userServices)
          )
        : this.spanFields;
    return this.getFieldsTableData(filteredFields, selectedOptions, skipOptions);
  }

  getBizEntityMetricTableData(selectedOptions: FieldPickerOptionData[]) {
    return this.bizMetrics.map(m => {
      const row: FieldPickerRow<"bizEntityMetric" | "userServiceMetric"> = {
        field: m,
        fieldLabel: m.metricName,
        fieldType: (m as UserServiceMetric).entityId ? "userServiceMetric" : "bizEntityMetric",
        rowSelected: Boolean(selectedOptions.find(op => isEqual(op.payload, m)))
      };

      return row;
    });
  }

  getPerfMetricTableData(selectedOptions: FieldPickerOptionData[]) {
    return this.perfMetrics.map(m => {
      const row: FieldPickerRow<"bizEntityMetric" | "userServiceMetric"> = {
        field: m,
        fieldLabel: m.metricName,
        fieldType: (m as UserServiceMetric).entityId ? "userServiceMetric" : "bizEntityMetric",
        rowSelected: Boolean(selectedOptions.find(op => isEqual(op.payload, m)))
      };

      return row;
    });
  }

  getBizEntityFieldsTableData(selectedOptions: FieldPickerOptionData[]) {
    return this.bizFields.map(f => {
      const propstype = f.bizField?.entityField?.propType;
      const customTypeName = f.bizField?.entityField?.kindDescriptor?.type || "not_set";
      const dataType = !propstype ? "NA" : propstype === "NA" ? "ENTITY" : propstype;
      const row: FieldPickerRow<"bizEntityField"> = {
        field: f,
        fieldLabel: FieldPickerUtils.getBizFieldLabel(f),
        fieldType: "bizEntityField",
        datatype: dataType,
        customDataType: customTypeName,
        propertySources: this.getBizFieldSourcesProperty(f),
        rowSelected: Boolean(selectedOptions.find(op => isEqual(op.payload, f)))
      };

      return row;
    });
  }

  getEntityUSField(bizEntityFieldName?: string): UserServiceField {
    const { entityType } = this.context || {};
    let expectedEntityType = "";

    if (entityType) {
      // Noun-first case
      expectedEntityType = entityType;
    } else {
      // Verb-first case
      expectedEntityType = ALL_USERSERVICES_ENTITY_TYPE_ID;
    }

    const allUSFields = [...this.usFields, ...this.usEntityFields, ...this.spanFields];

    const entityUsField = allUSFields.find(usField => {
      const { userServiceField } = usField;
      const { dataType, entityField, fieldName } = userServiceField;
      const { relNames, propType, entityType } = entityField || {};

      const derivedBizEntityFieldName = (bizEntityFieldName || "").split("$").slice(0, -1).join("$");
      const fieldNameMatches = bizEntityFieldName ? derivedBizEntityFieldName === fieldName : true;

      return (
        fieldNameMatches &&
        isEntityField(dataType) &&
        propType === "NA" &&
        relNames.length === 0 &&
        entityType === expectedEntityType
      );
    })?.userServiceField;

    if (!entityUsField) {
      logger.error("FieldPickerModel:getEntityUSField", "Could not find entity US field", entityType);
    }
    return entityUsField;
  }

  getEventIDField(): UserServiceField {
    const allUSFields = [...this.usFields, ...this.usEntityFields, ...this.spanFields];

    const eventIDUSField = allUSFields.find(
      usField => usField.userServiceField.fieldName === "eventID"
    )?.userServiceField;

    if (!eventIDUSField) {
      logger.error("FieldPickerModel:getEntityUSField", "Could not find eventID field");
    }
    return eventIDUSField;
  }

  groupFieldsByUserService() {
    const userServices = values(this.entityMap);
    let userServiceAndFieldsData: UserServiceWithFields[] = userServices.map((x: EntityMeta) => {
      const usPerfFields = this.getFieldsByUserServiceId(this.spanFields, x.entityId);
      const usFields = this.getFieldsByUserServiceId(this.usFields, x.entityId);
      const usEntityFields = this.getFieldsByUserServiceId(this.usEntityFields, x.entityId);
      const usJourneyFields = this.getFieldsByUserServiceId(this.journeyFields, x.entityId);
      return {
        usId: x.entityId,
        userServiceInfo: x,
        perfFields: usPerfFields,
        usFields,
        usEntityFields,
        usJourneyFields
      };
    }, []);

    userServiceAndFieldsData = userServiceAndFieldsData.filter(
      x => x.perfFields.length > 0 || x.usFields.length > 0 || x.usEntityFields.length > 0
    );
    return userServiceAndFieldsData;
  }

  getAllUserServiceFields(): UserServiceFieldWithMeta[] {
    return [...this.usFields, ...this.usEntityFields, ...this.spanFields, ...this.journeyFields];
  }

  getAllUserServiceFieldsTableData(): Array<FieldPickerRow<"userServiceField">> {
    const allFields = this.getAllUserServiceFields();
    return this.getFieldsTableData(allFields, []);
  }

  getUserServiceFieldsByType() {
    return {
      perfFields: this.spanFields,
      businessFields: this.usFields,
      entityFields: this.usEntityFields,
      journeyFields: this.journeyFields
    };
  }

  getAllUserservices() {
    return Object.values(this.entityMap || {});
  }

  private filterEntityUsFields(userServiceFields: UserServiceFieldWithMeta[]) {
    const usEntityFields: UserServiceFieldWithMeta[] = [];

    userServiceFields.forEach(usf => {
      const { userServiceField, userServiceMetadata } = usf;

      const { dataType, entityField } = userServiceField || {};

      const isBusinessField = userServiceMetadata?.business;
      const isEntityField = dataType === ENTITY_DATA_TYPE;
      const isValidPropType = entityField?.propType
        ? ALLOWED_DATA_TYPES.includes(usf.userServiceField?.entityField?.propType)
        : true;

      if (isBusinessField && isEntityField && isValidPropType) {
        usEntityFields.push(usf);
      }
    });
    usEntityFields.sort((x, y) => compare(x, y));
    return usEntityFields;
  }

  private filterBizUsFields(userServiceFields: UserServiceFieldWithMeta[]) {
    const usFields = userServiceFields.filter(
      usf =>
        usf.userServiceMetadata?.business &&
        usf.userServiceField?.dataType !== ENTITY_DATA_TYPE &&
        ALLOWED_DATA_TYPES.includes(usf.userServiceField?.dataType)
    );
    usFields.sort((x, y) => compare(x, y));
    return usFields;
  }

  private filterSpanUSFields(userServiceFields: UserServiceFieldWithMeta[]) {
    const usspanFields = userServiceFields.filter(
      usf =>
        usf.userServiceMetadata?.diagnostic &&
        ALLOWED_DATA_TYPES.includes(usf.userServiceField?.dataType) &&
        (usf.userServiceField?.dataType === ENTITY_DATA_TYPE
          ? usf.userServiceField?.entityField?.propType
            ? ALLOWED_DATA_TYPES.includes(usf.userServiceField?.entityField?.propType)
            : true
          : true)
    );
    usspanFields.sort((x, y) => compare(x, y));
    return usspanFields;
  }

  private filterJourneyUSFields(userServiceFields: UserServiceFieldWithMeta[]) {
    const usJourneyFields = userServiceFields.filter(
      usf =>
        usf.userServiceMetadata?.journey &&
        ALLOWED_DATA_TYPES.includes(usf.userServiceField?.dataType) &&
        (usf.userServiceField?.dataType === ENTITY_DATA_TYPE
          ? usf.userServiceField?.entityField?.propType
            ? ALLOWED_DATA_TYPES.includes(usf.userServiceField?.entityField?.propType)
            : true
          : true)
    );
    usJourneyFields.sort((x, y) => compare(x, y));
    return usJourneyFields;
  }

  private filterGroupByUSFields(userServiceFields: UserServiceFieldWithMeta[]) {
    const usFields: UserServiceFieldWithMeta[] = [];
    const fieldNames: Set<string> = new Set();

    userServiceFields.forEach(usf => {
      const { userServiceField } = usf;
      const name = userServiceField.fieldName;
      const supportedDataTypes: string[] = [CoreDataTypes.STRING, CoreDataTypes.ENTITY];
      const isEntityProp =
        userServiceField.dataType === CoreDataTypes.ENTITY && userServiceField?.entityField?.propName;
      const isBusinessField = usf.userServiceMetadata?.business;
      const isSupportedType = supportedDataTypes.includes(userServiceField.dataType);

      if (!fieldNames.has(name) && isBusinessField && isSupportedType && !isEntityProp) {
        usFields.push(usf);
        fieldNames.add(name);
      }
    });
    return usFields;
  }

  private getBizMetrics(bizMetrics: BizServiceMetric[], usMetrics: UserServiceMetric[]) {
    return [].concat(
      bizMetrics.filter(x => x.metricMetadata.category === "business"),
      usMetrics.filter(x => x.metricMetadata.category === "business")
    );
  }

  private getPerfMetrics(bizMetrics: BizServiceMetric[], usMetrics: UserServiceMetric[]) {
    return [].concat(
      bizMetrics.filter(x => x.metricMetadata.category === "softwarePerformance"),
      usMetrics.filter(x => x.metricMetadata.category === "softwarePerformance")
    );
  }

  private getFieldsByUserServiceId(list: UserServiceFieldWithMeta[], userServiceId: string) {
    const fields: UserServiceFieldWithMeta[] = [];
    list.forEach(x => {
      if (x.userServiceField.userServices.find(x => x.userServiceEntityId === userServiceId)) {
        fields.push(x);
      }
    });
    return fields;
  }

  private isContainingAllCommonUserServices(userServices: UserServiceTuple[], commonUserServices: UserServiceTuple[]) {
    return commonUserServices.every(commonService => userServices.find(service => isEqual(service, commonService)));
  }

  private getBizFieldSourcesProperty(bizfield: BizFieldInfo): PropertySource[] {
    const sources = bizfield.metadata?.bizFieldSources;
    if (sources?.length > 0) {
      return sources.map<PropertySource>(s => ({
        type: s.sourceType,
        name: s.sourceName,
        sourceId: null
      }));
    }
    return [];
  }

  private getFieldsTableData(
    list: UserServiceFieldWithMeta[],
    selectedOptions: FieldPickerOptionData[],
    skipOptions?: FieldPickerOptionData[]
  ) {
    const data: Array<FieldPickerRow<"userServiceField">> = [];

    list.forEach(field => {
      const { userServiceField } = field;

      const skipOption = skipOptions?.find(op => {
        const payload = omit(op.payload as UserServiceField, this.usfOmitProps);
        const curField = omit(userServiceField, this.usfOmitProps);
        return isEqual(payload, curField);
      });

      if (!skipOption) {
        const customDataType = field.userServiceField.entityField?.kindDescriptor?.type || "not_set";
        const selectedOption = selectedOptions.find(op => {
          const payload = omit(op.payload as UserServiceField, this.usfOmitProps);
          const curField = omit(userServiceField, this.usfOmitProps);
          return isEqual(payload, curField);
        });

        const row: FieldPickerRow<"userServiceField"> = {
          field: userServiceField,
          fieldType: "userServiceField",
          fieldLabel: FieldPickerUtils.getUserServiceFieldLabel(userServiceField),
          datatype: getUserServiceFieldDatatype(userServiceField),
          customDataType: customDataType,
          sources: this.getUsFieldSources(userServiceField),
          rowSelected: Boolean(selectedOption),
          selectedSources: selectedOption ? this.getUsFieldSources(selectedOption.payload as UserServiceField) : [],
          readOnly: selectedOption?.readOnly || false
        };
        data.push(row);
      }
    });

    return data;
  }
}
